/**
 * Infos sur l'artiste – A Propos modal box
*/
import css from '@css/sass/styles.module.scss'
import { CONSTANTS } from '@js/global'
import { AProposOptions } from '@js/types'
import { BaseBox, BaseBoxOptions } from './BaseBox'   // adjust path

export class APropos extends BaseBox {
  private _clickOutsideListener: ((e: MouseEvent) => void) | null = null
  private _hoverListener: (() => void) | null = null
  private _resizeObserver: ResizeObserver | null = null
  private statement: string = "undefined"
  private email: string = "undefined"

  // Font size boundaries
  private readonly FONT_MIN = 8   // px — below this, scrollbar appears
  private readonly FONT_MAX = 40   // px — above this, scrollbar appears

  constructor(options: AProposOptions) {
    const baseOptions: BaseBoxOptions = {
      id: css.apropos,
      title: 'à propos',
      bgColor: '#00a956',
      width: CONSTANTS.css.aproposWidth,
      widthMin: CONSTANTS.css.aproposWidthMin,
      heightOpen: 'auto',
      initialState: 'close',
      resizable: true,
    }
    super(baseOptions)
    options.object = this
    this.statement = options.data.statement
    this.email = options.data.email
  }

  protected createContent(): void {
    if (!this.box || !this.bodyContainer) return

    this.writeLine(this.statement, { tag: 'p', parent: this.bodyContainer })
    this.writeLine(
      `contact: <a href="mailto:${this.email}" target="_blank" rel="noopener noreferrer">${this.email}</a>`,
      { tag: 'p', parent: this.bodyContainer }
    )

    this.setupResizeObserver()
  }

  private setupResizeObserver(): void {
    if (!this.bodyContainer) return

    this._resizeObserver = new ResizeObserver(() => {
      this.fitText()
    })

    this._resizeObserver.observe(this.box!)
  }

  private fitText(): void {
    if (!this.bodyContainer || !this.box) return

    // Available area = box minus title bar
    const availW = this.box.clientWidth
    const availH = this.box.clientHeight - CONSTANTS.css.boxHeightMin

    if (availW <= 0 || availH <= 0) return

    // Reset to min before searching upward
    this.bodyContainer.style.fontSize = `${this.FONT_MIN}px`
    this.bodyContainer.style.overflow = 'hidden'

    let lo = this.FONT_MIN
    let hi = this.FONT_MAX
    let best = this.FONT_MIN

    while (lo <= hi) {
      const mid = Math.round((lo + hi) / 2)
      this.bodyContainer.style.fontSize = `${mid}px`

      const overflows =
        this.bodyContainer.scrollHeight > availH ||
        this.bodyContainer.scrollWidth > availW

      if (overflows) {
        hi = mid - 1
      } else {
        best = mid
        lo = mid + 1
      }
    }

    this.bodyContainer.style.fontSize = `${best}px`
    this.bodyContainer.style.overflowY = best === this.FONT_MIN ? 'auto' : 'hidden'
  }

  private writeLine(
    text: string,
    options: { tag?: string; parent?: HTMLElement } = {}
  ): void {
    const { tag = 'p', parent = this.bodyContainer } = options
    if (!parent) return
    const el = document.createElement(tag as keyof HTMLElementTagNameMap)
    el.innerHTML = text
    parent.appendChild(el)
  }

  protected onOpenChanged(isOpen: boolean): void {
    super.onOpenChanged(isOpen)

    if (isOpen) {
      if (this.bgLayer) this.bgLayer.style.opacity = '1'
      this.setupClickOutsideFade()
      // Refit after open transition settles
      requestAnimationFrame(() => this.fitText())
    } else {
      if (this.bgLayer) this.bgLayer.style.opacity = '1'
      this.removeClickOutsideAndHover()
    }
  }

  destroy(): void {
    this._resizeObserver?.disconnect()
    this._resizeObserver = null
    this.removeClickOutsideAndHover()
    super.destroy()
  }

  private setupClickOutsideFade(): void {
    if (!this.box || !this.bgLayer) return;

    // Define named functions (so we can remove them later)
    this._clickOutsideListener = (e: MouseEvent) => {
      if (this.isOpen && !this.box!.contains(e.target as Node)) {
        if (this.bgLayer) {
          this.bgLayer.style.opacity = '0';
        }
      }
    };

    this._hoverListener = () => {
      if (this.bgLayer) {
        this.bgLayer.style.opacity = '1';
      }
    };

    // Attach
    document.addEventListener('click', this._clickOutsideListener);
    this.box.addEventListener('mouseenter', this._hoverListener);
  }

  private removeClickOutsideAndHover(): void {
    if (this._clickOutsideListener) {
      document.removeEventListener('click', this._clickOutsideListener);
      this._clickOutsideListener = null;
    }

    if (this._hoverListener && this.box) {
      this.box.removeEventListener('mouseenter', this._hoverListener);
      this._hoverListener = null;
    }
  }

}